import type { z } from 'zod';

/**
 * 브라우저 저장소 래퍼.
 * - 비밀이 아닌 UI 선호값 · 힌트만 저장한다. **토큰을 저장하지 않는다.**
 *   (예외: 구 웹이 남긴 `LEGACY_SESSION` 을 읽고 지우는 것 — 쓰지는 않는다, DD-88)
 * - 키는 `STORAGE_KEY` 상수로만 접근한다. `localStorage` 를 직접 쓰지 않는다(lint 로 막는다).
 * - 읽을 때 스키마로 검증하고, 형태가 다르면 버리고 기본값을 돌려준다
 *   (배포로 형태가 바뀐 옛 값이 흰 화면을 만들지 않게).
 */
export const STORAGE_KEY = {
  /** 마지막으로 쓴 역할(로그인 화면 기본 선택용 힌트). 비밀이 아니다 */
  LAST_ROLE_HINT: 'd3.last-role-hint',
  /** 청크 로드 실패 복구의 새로고침 1회 표시 */
  CHUNK_RELOAD_GUARD: 'd3.chunk-reload-guard',
  /** 포트폴리오 메모의 임시 저장(회사마다 최근 100건). 이 브라우저에만 남는 작성 중 글이다(레거시 memoInput_<idx>) */
  MEMO_DRAFTS: 'd3.memo-drafts',
  /**
   * 구 웹 리프레시 토큰 — 로그인 이어받기(DD-88)에서 **읽고 지우기만** 한다. 2027-01-31 이후 코드와 함께 지운다.
   * 구 앱이 정한 키라 `d3.` 접두사가 없고, JSON 이 아닌 문자열 그대로 저장돼 있다(`raw`).
   */
  LEGACY_SESSION: 'session',
} as const;

export type StorageKey = (typeof STORAGE_KEY)[keyof typeof STORAGE_KEY];
export type StorageArea = 'local' | 'session';

export interface StorageItemOptions<T> {
  key: StorageKey;
  schema: z.ZodType<T>;
  fallback: T;
  area?: StorageArea;
  /**
   * 값을 JSON 으로 감싸지 않고 문자열 그대로 읽고 쓴다(T 는 string). 다른 앱이 남긴 키를 읽을 때만 쓴다.
   * 기본(JSON)으로 읽으면 JSON.parse 가 실패해 값을 지워 버린다.
   */
  raw?: boolean;
}

export interface StorageItem<T> {
  get(): T;
  set(value: T): void;
  remove(): void;
}

function resolveArea(area: StorageArea): Storage | null {
  try {
    // eslint-disable-next-line no-restricted-properties -- 저장소를 직접 만지는 곳은 이 파일 하나다
    return area === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    // 사생활 보호 모드 · 저장소 차단
    return null;
  }
}

export function defineStorageItem<T>(options: StorageItemOptions<T>): StorageItem<T> {
  const area = options.area ?? 'local';

  return {
    get() {
      const storage = resolveArea(area);
      if (!storage) return options.fallback;
      try {
        const raw = storage.getItem(options.key);
        if (raw === null) return options.fallback;
        const parsed = options.schema.safeParse(options.raw ? raw : JSON.parse(raw));
        if (parsed.success) return parsed.data;
        storage.removeItem(options.key);
        return options.fallback;
      } catch {
        try {
          storage.removeItem(options.key);
        } catch {
          // 지우지 못해도 기본값으로 계속한다.
        }
        return options.fallback;
      }
    },
    set(value) {
      try {
        resolveArea(area)?.setItem(
          options.key,
          options.raw ? String(value) : JSON.stringify(value),
        );
      } catch {
        // 용량 초과 · 차단. 선호값이므로 저장 실패는 무시한다.
      }
    },
    remove() {
      try {
        resolveArea(area)?.removeItem(options.key);
      } catch {
        // 무시
      }
    },
  };
}
